import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { templatesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Mail, RefreshCw, Copy, CheckCircle, Download,
  Send, ThumbsUp, ThumbsDown, Clock, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const EmailTemplatesPage = () => {
  const navigate = useNavigate();
  const { isSubscribed, user } = useAuth();
  const [templateType, setTemplateType] = useState('thank_you');
  const [companyName, setCompanyName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [interviewerName, setInterviewerName] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [generatedEmail, setGeneratedEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const templateTypes = [
    { value: 'thank_you', label: 'Thank You (Post-Interview)', icon: <ThumbsUp className="w-5 h-5" />, color: 'emerald' },
    { value: 'follow_up', label: 'Follow-Up', icon: <Clock className="w-5 h-5" />, color: 'blue' },
    { value: 'accept', label: 'Accept Offer', icon: <CheckCircle className="w-5 h-5" />, color: 'green' },
    { value: 'decline', label: 'Decline Offer', icon: <ThumbsDown className="w-5 h-5" />, color: 'slate' },
    { value: 'feedback', label: 'Request Feedback', icon: <Mail className="w-5 h-5" />, color: 'purple' },
    { value: 'raise', label: 'Request Raise', icon: <DollarSign className="w-5 h-5" />, color: 'amber' },
  ];

  const generateEmail = async () => {
    if (!interviewerName.trim()) {
      toast.error('Please enter the recipient\'s name');
      return;
    }
    setLoading(true);
    try {
      const res = await templatesAPI.getEmail({
        template_type: templateType,
        company_name: companyName,
        job_title: jobTitle,
        interviewer_name: interviewerName,
        additional_context: additionalContext,
        sender_name: user?.name || ''
      });
      setGeneratedEmail(res.data);
      setCopied(false);
    } catch (error) {
      toast.error('Failed to generate email');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (generatedEmail?.email_body) {
      navigator.clipboard.writeText(generatedEmail.email_body);
      setCopied(true);
      toast.success('Email copied to clipboard!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const getTemplateColor = (type) => {
    const template = templateTypes.find(t => t.value === type);
    return template?.color || 'slate';
  };

  if (!isSubscribed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
          <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Professional Email Templates</h2>
          <p className="text-slate-600 mb-6">Perfect emails for every situation. Subscribe to access this feature.</p>
          <Button onClick={() => navigate('/subscribe')} className="bg-amber-500 hover:bg-amber-600">
            Subscribe Now - R25 for 30 Days
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Professional Email Templates</h1>
            <p className="text-sm text-slate-500">Perfect emails for every career situation</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 lg:p-8">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Template Selection */}
          <div className="space-y-6">
            {/* Template Type */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Choose Template Type</h3>
              <div className="grid grid-cols-2 gap-3">
                {templateTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setTemplateType(type.value)}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                      templateType === type.value
                        ? `border-${type.color}-500 bg-${type.color}-50`
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`template-${type.value}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      templateType === type.value ? `bg-${type.color}-100 text-${type.color}-600` : 'bg-slate-100 text-slate-600'
                    }`}>
                      {type.icon}
                    </div>
                    <span className={`text-sm font-medium ${
                      templateType === type.value ? `text-${type.color}-700` : 'text-slate-700'
                    }`}>
                      {type.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Details Form */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Email Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="e.g., ABC Company"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="email-company"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Job Title
                  </label>
                  <input
                    type="text"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="e.g., Administrative Assistant"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="email-job-title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Recipient Name
                  </label>
                  <input
                    type="text"
                    value={interviewerName}
                    onChange={(e) => setInterviewerName(e.target.value)}
                    placeholder="e.g., Mr. Smith, Mrs. Nkosi"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="email-interviewer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Additional Context (Optional)
                  </label>
                  <textarea
                    value={additionalContext}
                    onChange={(e) => setAdditionalContext(e.target.value)}
                    placeholder="e.g., We discussed the upcoming marketing project..."
                    rows={3}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    data-testid="email-context"
                  />
                </div>

                <Button
                  onClick={generateEmail}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 py-6"
                  data-testid="generate-email-btn"
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Mail className="w-5 h-5 mr-2" />
                  )}
                  Generate Email
                </Button>
              </div>
            </div>
          </div>

          {/* Generated Email */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 h-fit sticky top-4">
            {generatedEmail ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Generated Email</h3>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const API_URL = process.env.REACT_APP_BACKEND_URL;
                          const response = await fetch(`${API_URL}/api/documents/download-pdf`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                            body: JSON.stringify({ content: `**Subject: ${generatedEmail.subject}**\n\n${generatedEmail.email_body}`, document_type: templateType })
                          });
                          if (!response.ok) throw new Error('PDF failed');
                          const blob = await response.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${templateType}-email.pdf`;
                          a.click();
                          URL.revokeObjectURL(url);
                          toast.success('PDF Downloaded!');
                        } catch { toast.error('Failed to download PDF'); }
                      }}
                      data-testid="download-email-pdf-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyToClipboard}
                      data-testid="copy-email-btn"
                    >
                      {copied ? (
                        <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      {copied ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                </div>

                {/* Subject */}
                {generatedEmail.subject && (
                  <div className="mb-4">
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Subject</label>
                    <p className="text-slate-900 font-medium bg-slate-50 rounded-lg p-3 mt-1">
                      {generatedEmail.subject}
                    </p>
                  </div>
                )}

                {/* Email Body */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Email Body</label>
                  <div className="bg-slate-50 rounded-xl p-4 mt-1 whitespace-pre-wrap text-slate-700 text-sm leading-relaxed max-h-[500px] overflow-y-auto">
                    {generatedEmail.email_body}
                  </div>
                </div>

                {/* Tips */}
                {generatedEmail.tips && generatedEmail.tips.length > 0 && (
                  <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="font-medium text-amber-800 text-sm mb-2">Tips for this email:</p>
                    <ul className="text-sm text-amber-700 space-y-1">
                      {generatedEmail.tips.map((tip, i) => (
                        <li key={i}>• {tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Your Email Will Appear Here</h3>
                <p className="text-slate-500 text-sm">
                  Select a template type and fill in the details to generate a professional email.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmailTemplatesPage;
